import {createApi} from "@reduxjs/toolkit/dist/query/react";
import {axiosBaseQuery} from "../index";
import {
  AdminUsersListParams,
  EditProfileRequest,
  LoginResponse,
  NotificationsResponse, Paginated,
  ResetPasswordData,
  Role,
  SuccessResponse,
  User,
  VerifyUserData
} from "../../types";
import {RegistrationFormData} from "../../pages/Auth/Registration";
import {format} from "date-fns";
import {
  ForgotPasswordFormData
} from "../../pages/Auth/ForgotPassword/components/ForgotPasswordForm";
import {ChangePasswordFormData} from "../../pages/User/ChangePassword/ChangePasswordForm";
import {ConcatFormData} from "../../pages/Contact/types";
import {UserFormData} from "../../admin/components/PageForm";
import {AdminUserEditData} from "../../admin/pages/Users/Edit/components/EditUserForm";

export interface LoginData {
  email: string
  password: string
  'g-recaptcha-response': string | null
}

export interface UserAttendanceCompletedCourse {
  id: number
  name: string
  pivot: {
    completed_at: string | null
  }
}

export interface UserAttendanceEvent {
  id: number
  name: string
  start_date: string
  course?: { id: number; name: string } | null
  eventType?: { id: number; name: string } | null
}

export interface UserAttendanceResponse {
  events: UserAttendanceEvent[]
  completed_courses: UserAttendanceCompletedCourse[]
}

export const UserApi = createApi({
  reducerPath: 'user',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['User', 'Notification', 'Users', 'UserAttendance'],
  endpoints: (build) => ({
    getUser: build.query<User, null>({
      query: () => ({url: `/user`, method: 'get'}),
      providesTags: ['User']
    }),
    adminUser: build.query<User, number>({
      query: (id) => ({url: `/admin/users/${id}`, method: 'get'}),
      providesTags: ['User']
    }),
    notifications: build.query<NotificationsResponse, null>({
      query: () => ({url: `/notifications`, method: 'get'}),
      providesTags: ['Notification']
    }),
    adminUsers: build.query<Paginated<User>, AdminUsersListParams>({
      query: ({search, page = 1, size = 10, order, orderBy}) => ({
        url: `/admin/users?page=${page}&size=${size}&search=${search}${order ? `&order=${order}` : ''}${orderBy ? `&orderBy=${orderBy}` : ''}`,
        method: 'get'
      }),
      providesTags: ['Users']
    }),
    markAsReadNotifications: build.mutation<SuccessResponse, null>({
      query: () => ({url: `/notifications`, method: 'put'}),
      invalidatesTags: ['Notification']
    }),
    login: build.mutation<LoginResponse, LoginData>({
      query: (data) => ({url: '/auth/login', method: 'post', data})
    }),
    changePassword: build.mutation<SuccessResponse, ChangePasswordFormData>({
      query: (data) => ({url: '/change-password', method: 'put', data})
    }),
    editProfile: build.mutation<SuccessResponse, EditProfileRequest>({
      query: (data) => ({url: '/user', method: 'put', data}),
      invalidatesTags: ['User']
    }),
    verifyUser: build.mutation<SuccessResponse, VerifyUserData>({
      query: ({token, hash, authToken}) => ({
        url: `/email/verify/${token}/${hash}`, method: 'post', requestHeaders: {
          'Authorization': `Bearer ${authToken}`
        }
      })
    }),
    forgotPassword: build.mutation<SuccessResponse, ForgotPasswordFormData>({
      query: (data) => ({url: '/auth/forgot-password', method: 'post', data})
    }),
    resetPassword: build.mutation<SuccessResponse, ResetPasswordData>({
      query: (data) => ({url: '/auth/reset-password', method: 'post', data})
    }),
    resendVerificationEmail: build.mutation<SuccessResponse, string>({
      query: (token) => ({
        url: '/email/verify/resend', method: 'post', requestHeaders: {
          'Authorization': `Bearer ${token}`
        }
      })
    }),
    registerUser: build.mutation<SuccessResponse, RegistrationFormData>({
      query: (data) => {
        return {
          url: '/auth/register',
          method: 'post',
          data: {...data, birthDate: format(data.birthDate, 'dd.LL.yyyy').toString()}
        }
      },
    }),
    sendContactMessage: build.mutation<SuccessResponse, ConcatFormData>({
      query: (data) => ({url: '/contact', method: 'post', data})
    }),
    sendLeadContact: build.mutation<SuccessResponse, { email: string }>({
      query: (data) => ({url: '/leads/contact', method: 'post', data}),
    }),
    editUser: build.mutation<SuccessResponse, { data: UserFormData, id: number }>({
      query: ({data, id}) => ({url: `/admin/users/${id}`, method: 'put', data}),
      invalidatesTags: ['Users', 'User']
    }),
    editAdminUser: build.mutation<User, {data: AdminUserEditData, id: number}>({
      query: ({data, id}) => ({url: `/admin/users/${id}`, method: 'put', data}),
      invalidatesTags: ['Users', 'User']
    }),
    deleteUser: build.mutation<SuccessResponse, { id: number }>({
      query: ({id}) => ({url: `/admin/users/${id}`, method: 'delete'}),
      invalidatesTags: ['Users']
    }),
    refreshUserNbs: build.mutation<SuccessResponse, { id: number }>({
      query: ({id}) => ({url: `/admin/users/${id}/refresh-nbs`, method: 'post'}),
      invalidatesTags: ['Users', 'User']
    }),
    adminRoles: build.query<Role[], void>({
      query: () => ({url: `/admin/roles`, method: 'get'}),
    }),
    syncUserRoles: build.mutation<SuccessResponse, { id: number, roleIds: number[] }>({
      query: ({id, roleIds}) => ({
        url: `/admin/users/${id}/roles`,
        method: 'put',
        data: {role_ids: roleIds},
      }),
      invalidatesTags: ['Users', 'User']
    }),
    userAttendance: build.query<UserAttendanceResponse, number>({
      query: (id) => ({url: `/admin/users/${id}/attendance`, method: 'get'}),
      providesTags: (_r, _e, id) => [{type: 'UserAttendance', id}]
    }),
    markCourseCompleted: build.mutation<SuccessResponse, { userId: number, courseId: number }>({
      query: ({userId, courseId}) => ({url: `/admin/users/${userId}/courses/${courseId}/complete`, method: 'post'}),
      invalidatesTags: (_r, _e, {userId}) => [{type: 'UserAttendance', id: userId}]
    }),
    unmarkCourseCompleted: build.mutation<SuccessResponse, { userId: number, courseId: number }>({
      query: ({userId, courseId}) => ({url: `/admin/users/${userId}/courses/${courseId}/complete`, method: 'delete'}),
      invalidatesTags: (_r, _e, {userId}) => [{type: 'UserAttendance', id: userId}]
    }),
  })
});

export const {
  useGetUserQuery,
  useNotificationsQuery,
  useAdminUsersQuery,
  useAdminUserQuery,
  useEditAdminUserMutation,
  useLoginMutation,
  useForgotPasswordMutation,
  useVerifyUserMutation,
  useResetPasswordMutation,
  useMarkAsReadNotificationsMutation,
  useResendVerificationEmailMutation,
  useRegisterUserMutation,
  useChangePasswordMutation,
  useEditProfileMutation,
  useSendContactMessageMutation,
  useSendLeadContactMutation,
  useDeleteUserMutation,
  useRefreshUserNbsMutation,
  useUserAttendanceQuery,
  useMarkCourseCompletedMutation,
  useUnmarkCourseCompletedMutation,
  useAdminRolesQuery,
  useSyncUserRolesMutation,
} = UserApi;
